<script setup>
import { ref, computed } from 'vue'
import layoutEnglish from '@/locales/keyboard/en.js';
import layoutSlovenian from '@/locales/keyboard/si.js';
//https://tailwindcomponents.com/component/macbook-keyboard

const props = defineProps({
  id: {
    type: String,
    default: null,
  },
  name: {
    type: String,
    default: null,
  },
  layout: {
    type: String,
    default: "sl", //sl, en
  },
  modifierMode: {
    type: String,
    default: "press",
    //"press" - press to enable, press again to turn off, or after any other non-modifier key clear modifiers
    //"hold" - user need to hold down the key to activate modifier, when key is released the modifier flag is also cleared
  },
  initialStep: { //Delay between first press and next counting step
    type: Number,
    default: 300
  },
  normalStep: { //Delay between every consecutive counting step
    type: Number,
    default: 150
  },
  fastStep: { //Delay between every consecutive counting step when switched to fast mode
    type: Number,
    default: 50
  },
  fastAfter: { //Delay from initial press to shift to fast mode
    type: Number,
    default: 800
  },
});

const emit = defineEmits([ "keypress", "keydown", "keyup", "mousedown" ]);

defineExpose({ reset });

function reset() {
  isCapsLock.value = false;
  modifiers.value = {
    ctrl: false,
    alt: false,
    shift: false,
    meta: false
  };
}

const modifiers = ref({
  ctrl: false,
  alt: false,
  shift: false,
  meta: false
});

const isCapsLock = ref(false);

const layoutSelected = computed(() => {
  return props.layout == "sl" ? layoutSlovenian : layoutEnglish;
});

function layoutPartial(start, end) {
  let list = [ ];
  for (let i = start; i <= end; i++) {
    list.push(layoutSelected.value[i]);
  }
  return list;
}

function isAlphanumeric(char) {
  return /^[\w\dčćđšž]{1}$/gi.test(char);
}

function caseSensitive(letter) {
  return isCapsLock.value || modifiers.value.shift ? letter.toUpperCase() : letter.toLowerCase();
}

function getKeyData(e) {
  let special = false;
  let modifier = false;
  let ctrlAltShift = modifiers.value;

  let char = e.target.dataset.char; //Normal version
  let shift = e.target.dataset.shift; //Shift alternative

  //Special char starting with ":", e.g. ":enter" or ":backspace"
  if (char?.length > 1 && char[0] == ":") {
    special = true;
    modifier = [ ":ctrl", ":alt", ":shift", ":meta" ].includes(char); //Modifier key
  }

  //Toggle lowercase/uppercase/alternative letter due to the shift key and/or caps lock key
  let alphanumeric = isAlphanumeric(char);
  if (modifiers.value.shift && shift) {
    char = shift; //Shift key
  }
  else if (alphanumeric) {
    char = caseSensitive(char);
  }

  return {
    char, //Pressed key
    alphanumeric, //Is press key an alphanumeric char (A-Z, 0-9) ? true or false
    special, //Is press key a special key such as enter or backspace? true or false
    modifier, //Is press key a modifier key such as ctrl/alt/delete? true or false
    modifiers: ctrlAltShift //Current state of modifier keys
  };
}

function keypress(e) {
  let resetModifiers = false;

  let char = e.target.dataset.char;
  if (char == ":capslock") {
    isCapsLock.value = !isCapsLock.value;
  }
  if (props.modifierMode == "press") {
    let char = e.target.dataset.char;
    if (char == ":ctrl") {
      modifiers.value.ctrl ^= true;
    }
    else if (char == ":alt") {
      modifiers.value.alt ^= true;
    }
    else if (char == ":shift") {
      modifiers.value.shift ^= true;
    }
    else {
      resetModifiers = true;
    }
  }

  //CTRL+ALT+DEL
  if (modifiers.value.ctrl && modifiers.value.alt && char == ":del") {
    showEasterEgg();
  }

  //Emit the keypress event
  emit("keypress", getKeyData(e));

  //Reset modifiers after key is processed
  if (resetModifiers) {
    modifiers.value.ctrl = false;
    modifiers.value.alt = false;
    modifiers.value.shift = false;
  }
}

function keydown(e) {
  if (props.modifierMode == "hold") {
    let char = e.target.dataset.char;
    if (char == ":ctrl") {
      modifiers.value.ctrl = true;
    }
    if (char == ":alt") {
      modifiers.value.alt = true;
    }
    if (char == ":shift") {
      modifiers.value.shift = true;
    }
  }

  emit("keydown", getKeyData(e));

  lastKeyData = getKeyData(e);
  start();
}

function keyup(e) {
  end();

  if (props.modifierMode == "hold") {
    let char = e.target.dataset.char;
    if (char == ":ctrl") {
      modifiers.value.ctrl = false;
    }
    if (char == ":alt") {
      modifiers.value.alt = false;
    }
    if (char == ":shift") {
      modifiers.value.shift = false;
    }
  }
  emit("keyup", getKeyData(e));
}

function sizeClass(w, h) {
  //return "w-16 h-16";
  let adj = 4;
  return `w-${w} h-${h}`;
}

function size(w, h) {
  //let f = 1; //Scale factor
  //let f = 0.75; //Scale factor - fits exactly on 768px width
  let f = 0.70; //Scale factor - fits to 720px width - min resolution
  if (w == 100) w = 78;
  return `width: ${w * f * 0.25}rem; height: ${h * f * 0.25}rem;`;
}

let lastKeyData = null;
let initialTimer = null;
let normalTimer = null;
let fastTimer = null;
let pressed = false;

function repeatLastKey() {
  if (lastKeyData) {
    emit("keypress", lastKeyData);
    emit("keydown", lastKeyData);
  }
}

function startStepTimer() {
  //Normal interval steps
  if (props.normalStep > 0) {
    normalTimer = setInterval(function() {
      if (pressed) {
        repeatLastKey();
      }
    }, props.normalStep);
  }

  //Shift to speed up steps
  if (props.fastAfter > 0) {
    initialTimer = setTimeout(() => {
      if (pressed) {
        //repeatLastKey();
        startFastTimer();
      }
    }, props.fastAfter);
  }
}

function startFastTimer() {
  if (props.fastStep > 0) {
    if (normalTimer) {
      clearInterval(normalTimer);
      normalTimer = null;
    }

    fastTimer = setInterval(function() {
      if (pressed) {
        repeatLastKey();
      }
    }, props.fastStep);
  }
}

function start() {
  //repeatLastKey();
  pressed = true;
  if (props.initialStep > 0) {
    initialTimer = setTimeout(() => {
      if (pressed) {
        repeatLastKey();
        startStepTimer();
      }
    }, props.initialStep);
  }
  else {
    startStepTimer();
  }
}

function end() {
  pressed = false;
  if (initialTimer) {
    clearTimeout(initialTimer);
    initialTimer = null;
  }
  if (normalTimer) {
    clearInterval(normalTimer);
    normalTimer = null;
  }
  if (fastTimer) {
    clearInterval(fastTimer);
    fastTimer = null;
  }
}

const easterEgg = ref(false);
function showEasterEgg() {
  if (!easterEgg.value) {
    easterEgg.value = true;
    setTimeout(() => easterEgg.value = false, 15000 / 2);
  }
}
</script>

<template>
  <div class="macbook compact relative flex flex-col" @mousedown.prevent="() => emit('mousedown')">
    <div class="keyboard-panel mb-7">
      
      <div class="keyboard flex flex-col pt-1 text-sm">
        <div class="flex flex-row justify-between">
          <button :style="size(16, 16)" class="key normal text-center" @click="keypress" @mousedown="keydown" @mouseup="keyup" :data-char="key.char" :data-shift="key.shift" v-for="key in layoutPartial(0, 12)">{{ key.shift }}<br>{{ key.normal }}</button>
          <button :style="size(24, 16)" class="key special text-right pr-3 text-xs" @click="keypress" @mousedown="keydown" @mouseup="keyup" data-char=":backspace"><i class="fa fa-arrow-left"></i></button>
        </div>
        <div class="flex flex-row justify-between">
          <button :style="size(24, 16)" class="key special text-left pl-3 text-xs"><i class="fa fa-right-left"></i></button>
          <button :style="size(16, 16)" class="key normal text-center" @click="keypress" @mousedown="keydown" @mouseup="keyup" :data-char="key.char" :data-shift="key.shift" v-for="key in layoutPartial(13, 22)">{{ caseSensitive(key.normal) }}</button>
          <button :style="size(16, 16)" class="key normal text-center" @click="keypress" @mousedown="keydown" @mouseup="keyup" :data-char="key.char" :data-shift="key.shift" v-for="key in layoutPartial(23, 25)">{{ key.shift }}<br v-if="key.shift && key.normal">{{ key.caseSensitive ? caseSensitive(key.normal) : key.normal }}</button>
        </div>
        <div class="flex flex-row justify-between">
          <button :style="size(28, 16)" class="key special text-left pl-3 text-xs" :class="{ 'modifier-hold': isCapsLock }" @click="keypress" @mousedown="keydown" @mouseup="keyup" data-char=":capslock">Caps Lock</button>
          <button :style="size(16, 16)" class="key normal text-center" @click="keypress" @mousedown="keydown" @mouseup="keyup" :data-char="key.char" :data-shift="key.shift" v-for="key in layoutPartial(26, 34)">{{ caseSensitive(key.normal) }}</button>
          <button :style="size(16, 16)" class="key normal text-center" @click="keypress" @mousedown="keydown" @mouseup="keyup" :data-char="key.char" :data-shift="key.shift" v-for="key in layoutPartial(35, 36)">{{ key.shift }}<br v-if="key.shift && key.normal">{{ key.caseSensitive ? caseSensitive(key.normal) : key.normal }}</button>
          <button :style="size(28, 16)" class="key special text-right pr-3" @click="keypress" @mousedown="keydown" @mouseup="keyup" data-char=":enter"><i class="fa fa-turn-down fa-rotate-90"></i></button>
        </div>
        <div class="flex flex-row justify-between">
          <button :style="size(34, 16)" class="key special text-left pl-3 text-xs" :class="{ 'modifier-hold': modifiers.shift }" @click="keypress" @mousedown="keydown" @mouseup="keyup" data-char=":shift"><i class="fa fa-up-long"></i></button>
          <button :style="size(16, 16)" class="key normal text-center" @click="keypress" @mousedown="keydown" @mouseup="keyup" :data-char="key.char" :data-shift="key.shift" v-for="key in layoutPartial(37, 43)">{{ caseSensitive(key.normal) }}</button>
          <button :style="size(16, 16)" class="key normal text-center" @click="keypress" @mousedown="keydown" @mouseup="keyup" :data-char="key.char" :data-shift="key.shift" v-for="key in layoutPartial(44, 46)">{{ key.shift }}<br v-if="key.shift && key.normal">{{ key.normal }}</button>
          <button :style="size(34, 16)" class="key special text-right pr-3 text-xs" :class="{ 'modifier-hold': modifiers.shift }" @click="keypress" @mousedown="keydown" @mouseup="keyup" data-char=":shift"><i class="fa fa-up-long"></i></button>
        </div>
        <div class="flex flex-row justify-between">
          <button :style="size(18, 16)" class="key special text-left pl-3 text-xs" :class="{ 'modifier-hold': modifiers.ctrl }" @click="keypress" @mousedown="keydown" @mouseup="keyup" data-char=":ctrl">CTRL</button>
          <button :style="size(18, 16)" class="key special text-center text-xs" :class="{ 'modifier-hold': modifiers.alt }" @click="keypress" @mousedown="keydown" @mouseup="keyup" data-char=":alt">ALT</button>
          <button :style="size(136, 16)" class="key special text-center" @click="keypress" @mousedown="keydown" @mouseup="keyup" data-char=" "></button>
          <button :style="size(16, 16)" class="key special text-center text-xs" @click="keypress" @mousedown="keydown" @mouseup="keyup" data-char=":del">DEL</button>
          <button :style="size(16, 16)" class="key special text-center" @click="keypress" @mousedown="keydown" @mouseup="keyup" data-char=":left"><i class="fa fa-caret-left"></i></button>
          <div class="flex flex-col">
            <button :style="size(16, 8)" class="key-small rounded-t-lg" @click="keypress" @mousedown="keydown" @mouseup="keyup" data-char=":up"><i class="fa fa-caret-up"></i></button>
            <button :style="size(16, 8)" class="key-small rounded-b-lg" @click="keypress" @mousedown="keydown" @mouseup="keyup" data-char=":down"><i class="fa fa-caret-down"></i></button>
          </div>
          <button :style="size(16, 16)" class="key special text-center" @click="keypress" @mousedown="keydown" @mouseup="keyup" data-char=":right"><i class="fa fa-caret-right"></i></button>
        </div>
      </div>
    </div>

    <div class="absolute -top-10 left-0 spin-run" v-if="easterEgg">
      <i class="fa fa-face-smile fa-spin text-amber-500" style="font-size: 32px;"></i>
    </div>
      
  </div>
</template>

<style scoped>
body {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
}

.spin-run {
  animation: linear infinite;
  animation-name: run;
  animation-duration: 15s;
}

@keyframes run {
  0% {
    left: 0;
  }
  50% {
    left: 100%;
  }
  100% {
    left: 0;    
  }
}
  
.macbook {
  padding-top: 35px;
  padding-left: 35px;
  padding-right: 35px;
  
  /* width: 85%; */
  width: 1100px;
  /* background: rgb(190,190,190); */
}

.macbook.compact {
  padding-top: 10px;
  padding-left: 10px;
  padding-right: 10px;
  /* width: 768px; */
  width: 720px;
}

.key.normal {
  @apply my-0 rounded-lg bg-black text-white dark:bg-slate-300 dark:text-slate-900;
  @apply transition-all duration-500 active:duration-[0s] active:bg-sky-800 dark:active:bg-slate-300/70;
  /* @apply active:scale-95; */
}

.key.special {
  @apply my-0 rounded-lg bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900;
  @apply transition-all duration-500 active:duration-[0s] active:bg-sky-900 dark:active:bg-slate-300/70;
  /* @apply active:scale-95; */
}

.key > * {
  pointer-events: none;
}

.key.modifier-hold {
  @apply !bg-sky-800 dark:!bg-blue-300 dark:text-blue-800;
}

.key-small {
  @apply bg-slate-800 border-b border-gray-700 text-center text-sm text-white dark:bg-slate-100 dark:text-slate-900 dark:border-slate-800;
  @apply transition-all duration-500 active:duration-[0s] active:bg-sky-900 dark:active:bg-slate-300/70;
  /* @apply active:scale-95; */
}

.bg-mac-gray {
  background-color: #888;
}

.keyboard-panel {
  width: 100%;
  box-shadow: inset 0 0 10px #999;
  border-radius: 5px;
  padding: 10px;
}

.dark .keyboard-panel {
  box-shadow: inset 0 0 10px #777;
}

.macbook.compact .keyboard-panel {
  border-radius: 3px;
  padding: 4px 8px 10px 8px;
}

.macbook.compact .keyboard-panel .keyboard {
  @apply gap-1;
}

.touch-bar {
  background: rgba(0, 0, 0, 0.8);
  border-radius: 5px;
}   
  
button {
  outline: none;
}

.w-28 {
  width: 110px;
}

.w-34 {
  width: 140px;
}

.w-18 {
  width: 70px;
}

.w-100 {
  width: 350px;
}
</style>
